import cardService from '@/services/card.service'
import { FilterType } from '@/types/additionalFilters'
import { CardListType, CardType, PageCardType, PriceMapType } from '@/types/card'

const filterFuncs = (filters: FilterType[], priceSource: keyof PriceMapType): Array<(card: PageCardType) => boolean> =>
  filters.map(({ type, values }) => {
    if (!values) return () => true
    switch (type) {
      case 'cardName':
        return (card: PageCardType): boolean => {
          if (!card) return false // Shouldn't really happen, but currently does with MDFC back-sides
          const names = card.names || [card.name]
          return names.some((name) => name.toLowerCase().includes((values.value as string).toLowerCase()))
        }
      case 'typeLine':
        return (card: PageCardType): boolean => {
          if (!card) return false // Shouldn't really happen, but currently does with MDFC back-sides
          return card.type.toLowerCase().includes((values.value as string).toLowerCase())
        }
      case 'universesBeyond':
        return (card: PageCardType): boolean => {
          if (!card) return false // Shouldn't really happen, but currently does with MDFC back-sides
          return !values.checked || !card.universes_beyond
        }
      case 'colors':
        return (card: PageCardType) => {
          if (!card) {
            return false // Shouldn't really happen, but currently does with MDFC back-sides
          } else if (card.color_identity === undefined) {
            return values.c || values.operator === 'SOME'
          } else {
            switch (values.operator) {
              case 'EXCLUSIVE ALL':
                return ['w', 'u', 'b', 'r', 'g'].every(
                  (c) => values[c as keyof FilterType['values']] === card.color_identity.includes(c.toUpperCase()),
                )
              case 'INCLUSIVE ALL':
                return ['w', 'u', 'b', 'r', 'g'].every(
                  (c) => !values[c as keyof FilterType['values']] || card.color_identity.includes(c.toUpperCase()),
                )
              case 'SOME':
                return ['w', 'u', 'b', 'r', 'g'].every(
                  (c) => values[c as keyof FilterType['values']] || !card.color_identity.includes(c.toUpperCase()),
                )
              default:
                console.warn('[Additional Filters] Unknown operator')
                return true
            }
          }
        }
      case 'manaValue':
      case 'power':
      case 'price':
      case 'salt':
      case 'inclusion':
      case 'synergy':
      case 'lift':
      case 'num_decks':
      case 'toughness':
        return (card: PageCardType): boolean => {
          if (!card) return false // Shouldn't really happen, but currently does with MDFC back-sides
          if (!values.operator || !values.value) return true
          const cardValue =
            (type === 'manaValue'
              ? card?.cmc
              : type === 'price'
                ? card.prices[priceSource]?.price || 0
                : type === 'inclusion'
                  ? (100 * card.num_decks) / card.potential_decks
                  : type === 'synergy'
                    ? 100 * card.synergy!
                    : type === 'lift'
                      ? card.lift!
                      : card[type]) || 0
          // eslint-disable-next-line no-eval
          return eval(`${parseFloat(cardValue as string)}${values.operator}${parseFloat(values.value as string)}`)
        }
      case 'rarity':
        return (card: PageCardType) => {
          if (!card) return false // Shouldn't really happen, but currently does with MDFC back-sides
          return card?.rarity === values.value
        }
      case 'sets':
        return (card: PageCardType) => {
          if (!card) return false // Shouldn't really happen, but currently does with MDFC back-sides
          switch (values.operator) {
            case 'INCLUDE':
              return !!values.sets?.some((set) => card?.sets?.includes(set))
            case 'EXCLUDE':
              return !values.sets?.some((set) => card?.sets?.includes(set))
            default:
              console.warn('[Additional Filters] Unknown operator')
              return true
          }
        }
      case 'tags':
        return (card: PageCardType): boolean => {
          if (!card) return false // Shouldn't really happen, but currently does with MDFC back-sides
          return (values.value as string[]).some((tag) => card?.tags?.includes(tag))
        }
      default:
        console.warn('[Additional Filters] Unknown type')
        return (_card: PageCardType) => true
    }
  })

type Props = {
  cardLists: CardListType[]
  cardMap: Record<string, CardType>
  filters: FilterType[]
  initCardLists: CardListType[]
  priceSource: keyof PriceMapType
}

const filter = ({ cardLists, cardMap, filters, initCardLists, priceSource }: Props): CardListType[] => {
  if (filters.length === 0) return cardLists

  const pageCardMap = cardService.getPageCardMap(cardMap, initCardLists)

  return cardLists.map((cardlist) => ({
    ...cardlist,
    cardviews: cardlist.cardviews.filter((cardview) =>
      filterFuncs(filters, priceSource).every((filterFunc, i) => {
        if (cardview.cards) {
          const cardFaces = cardview.cards.map((card) => pageCardMap[card.url])
          return filters[i].type === 'colors' && (filters[i].values?.operator as string) === 'SOME'
            ? cardFaces.every(filterFunc)
            : cardFaces.some(filterFunc)
        }
        const card = pageCardMap[cardview.sanitized]
        if (!card) {
          console.warn(`[Additional Filters] Card not found: ${cardview.sanitized}`)
          return false
        }
        return filterFunc(card)
      }),
    ),
  }))
}

export default filter
